import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Subject, take, takeUntil } from 'rxjs';

import { AsyncPipe, DatePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
// eslint-disable-next-line max-len
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import {
  hasCropFieldsArray,
  hasDateAndCropField,
  NotificationCategory,
  NotificationDto,
  NotificationType,
} from '../../shared/models/models';
import { loadNotifications } from '../../store/tga.actions';
import { getTgaCropYear, selectAllNotifications } from '../../store/tga.selectors';
import { BackArrowButtonComponent } from '../back-arrow-button/back-arrow-button.component';
import { CropYearSelectorComponent } from '../../../shared/components/crop-year-selector/crop-year-selector.component';

/**
 * Notification types that are excluded from the history view.
 * Add types here to hide them from both the Warnings and Notifications history tabs.
 */
const NOTIFICATION_HISTORY_EXCLUDED_TYPES = new Set<NotificationType>([
  NotificationType.SatelliteImagesAvailable,
]);

interface NotificationHistoryItem {
  type: NotificationType;
  date: Date | undefined;
  cropFieldName?: string;
  notification: NotificationDto;
}

enum HistoryTab {
  Warnings = 0,
  Notifications = 1,
}

@Component({
  selector: 'app-notification-history',
  templateUrl: './notification-history.component.html',
  styleUrl: './notification-history.component.scss',
  imports: [
    BackArrowButtonComponent,
    CropYearSelectorComponent,
    MatButton,
    NotificationEmptyComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class NotificationHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  readonly activeTabIndex = signal<number>(HistoryTab.Warnings);
  readonly tabs = [
    { key: 'Warnings', index: HistoryTab.Warnings },
    { key: 'Notifications', index: HistoryTab.Notifications },
  ];

  displayedColumns: string[] = ['date', 'type', 'cropField'];
  pageSize = 10;

  private readonly destroy$ = new Subject<void>();
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly locale = inject(LocaleService);
  localeString: string;

  private readonly allNotifications = signal<NotificationDto[]>([]);

  private readonly warningsData = computed<NotificationHistoryItem[]>(() => {
    const warnings = this.allNotifications().filter(
      (n) => (n.category || NotificationCategory.Warning) === NotificationCategory.Warning,
    );
    return this.mapNotificationsToTableItems(warnings);
  });

  private readonly notificationsData = computed<NotificationHistoryItem[]>(() => {
    const notifications = this.allNotifications().filter((n) => n.category === NotificationCategory.Message);
    return this.mapNotificationsToTableItems(notifications);
  });

  private readonly page$ = new BehaviorSubject<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: 0,
  });

  private readonly sort$ = new BehaviorSubject<Sort>({
    active: '',
    direction: '',
  });

  private readonly queryParams$ = this.route.queryParams.pipe(
    takeUntil(this.destroy$),
  );

  private readonly activeTabData$ = combineLatest([
    this.store.select(selectAllNotifications),
    toObservable(this.activeTabIndex),
    this.queryParams$,
  ]).pipe(
    map(([all, activeTab, params]) => {
      // Handle query params for tab selection
      if (params['tab'] === 'messages') {
        this.activeTabIndex.set(HistoryTab.Notifications);
        activeTab = HistoryTab.Notifications;
      } else if (params['tab'] === 'warnings') {
        this.activeTabIndex.set(HistoryTab.Warnings);
        activeTab = HistoryTab.Warnings;
      }
      this.allNotifications.set(all);
      return activeTab === HistoryTab.Warnings ? this.warningsData() : this.notificationsData();
    }),
  );

  private readonly sortedData$ = combineLatest([this.activeTabData$, this.sort$]).pipe(
    map(([data, sortState]) => {
      if (!sortState.active || sortState.direction === '') {
        return data;
      }

      return [...data].sort((a, b) => {
        const isAsc = sortState.direction === 'asc';
        const valueA = this.getSortValue(a, sortState.active);
        const valueB = this.getSortValue(b, sortState.active);

        if (valueA < valueB) {
          return isAsc ? -1 : 1;
        }
        if (valueA > valueB) {
          return isAsc ? 1 : -1;
        }
        return 0;
      });
    }),
  );

  readonly paginatedData$ = combineLatest([this.sortedData$, this.page$]).pipe(
    map(([allData, pageEvent]) => {
      const startIndex = pageEvent.pageIndex * pageEvent.pageSize;
      return allData.slice(startIndex, startIndex + pageEvent.pageSize);
    }),
  );

  readonly totalLength$ = this.sortedData$.pipe(map((data) => data.length));

  constructor() {
    this.localeString = this.locale.getLocale();
  }

  ngOnInit(): void {
    this.initializeLocale();
    this.loadNotifications();
    this.initializePagination();
  }

  ngAfterViewInit(): void {
    this.initializeSorting();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTab(index: number): void {
    if (index < 0 || index >= this.tabs.length) {
      return;
    }
    this.activeTabIndex.set(index);
    this.resetPagination();

    const tabParam = index === HistoryTab.Warnings ? 'warnings' : 'messages';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabParam },
      queryParamsHandling: 'merge',
    });
  }

  isTabActive(index: number): boolean {
    return this.activeTabIndex() === index;
  }

  page(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.page$.next(event);
  }

  sortData(sort: Sort): void {
    this.sort$.next(sort);
    this.resetPagination();
  }

  navigateToTGAInsights = (): void => {
    this.router.navigate(['/tga']);
  };

  getNotificationTypeLabel(type: NotificationType): string {
    return this.locale.translate(`TGA.Notifications.Types.${type}`);
  }

  getEmptyStateKey(): string {
    const key = this.activeTabIndex() === HistoryTab.Warnings ? 'EmptyWarnings' : 'EmptyNotifications';
    return `TGA.Notifications.History.${key}`;
  }

  private initializeLocale(): void {
    this.locale.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.localeString = this.locale.getLocale();
    });
  }

  private loadNotifications(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((cropYear) => this.store.dispatch(loadNotifications({ cropYear })));
  }

  private initializePagination(): void {
    this.resetPagination();
  }

  private resetPagination(): void {
    this.page$.next({
      pageIndex: 0,
      pageSize: this.pageSize,
      length: 0,
    });
  }

  private initializeSorting(): void {
    // Initialize sorting when ViewChild is available
    if (this.sort) {
      this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe((sort: Sort) => {
        this.sortData(sort);
      });
    }
  }

  private getSortValue(item: NotificationHistoryItem, property: string): string | number {
    switch (property) {
      case 'cropField':
        return item.cropFieldName || '';
      case 'date':
        return item.date ? item.date.getTime() : 0;
      case 'type':
        return item.type;
      default:
        return '';
    }
  }

  private mapNotificationsToTableItems(notifications: NotificationDto[]): NotificationHistoryItem[] {
    return notifications
      .filter((notification) => notification.isClosed && !NOTIFICATION_HISTORY_EXCLUDED_TYPES.has(notification.type))
      .map((notification) => ({
        type: notification.type,
        date: this.getNotificationDate(notification),
        cropFieldName: this.getCropFieldName(notification),
        notification,
      }));
  }

  private getNotificationDate(notification: NotificationDto): Date {
    if (notification.createdAt) {
      const date = new Date(notification.createdAt);
      if (date instanceof Date && !Number.isNaN(date.getTime()) && date.getFullYear() > 1) {
        return date;
      }
    }
    return new Date();
  }

  private getCropFieldName(notification: NotificationDto): string | undefined {
    if (hasDateAndCropField(notification)) {
      return notification.cropFieldName;
    }
    if (hasCropFieldsArray(notification)) {
      const cropFields = notification.cropFields;
      if (cropFields && cropFields.length > 0) {
        return cropFields.length === 1
          ? cropFields[0].cropFieldName
          : this.locale.translate('TGA.Notifications.MultipleCropFields', { count: cropFields.length.toString() });
      }
    }
    return undefined;
  }
}
